import React from 'react';
import { Card } from '@vehix/portal/components/ui/card';
import { cn } from '@vehix/portal/lib/utils';
import type { RefreshOutcomeEntry } from '@vehix/shared/types/recallSnapshots';

interface Props {
  entries: RefreshOutcomeEntry[];
}

const STATUS_COLOR: Record<RefreshOutcomeEntry['status'], string> = {
  completed: 'bg-emerald-500',
  failed: 'bg-red-500',
  running: 'bg-amber-400 animate-pulse',
  other: 'bg-muted-foreground/40'
};

function fmtDuration(s: number | null): string {
  if (s == null) return '—';
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s % 60)}s`;
}

export const RefreshOutcomeRibbon: React.FC<Props> = ({ entries }) => {
  return (
    <Card className='p-4'>
      <div className='flex items-baseline justify-between gap-2 mb-3'>
        <h3 className='text-sm font-semibold'>Recent refreshes</h3>
        <span className='text-[11px] text-muted-foreground'>
          {entries.length === 0 ? 'No runs yet' : `last ${entries.length}`}
        </span>
      </div>

      {entries.length === 0 ? (
        <p className='text-xs text-muted-foreground'>No refresh executions in the recent window.</p>
      ) : (
        <div className='flex flex-wrap gap-1.5'>
          {entries.map(e => (
            <span
              key={e.id}
              className={cn('h-3 w-3 rounded-sm', STATUS_COLOR[e.status])}
              title={[
                `${e.tenant_name}${e.oem ? ' · ' + e.oem : ''}`,
                e.task_name,
                `status: ${e.status}`,
                `duration: ${fmtDuration(e.duration_seconds)}`,
                e.started_at ? `started: ${new Date(e.started_at).toLocaleString()}` : undefined,
                e.error_summary && Object.keys(e.error_summary).length > 0
                  ? `error: ${JSON.stringify(e.error_summary).slice(0, 200)}`
                  : undefined
              ]
                .filter(Boolean)
                .join('\n')}
            />
          ))}
        </div>
      )}
    </Card>
  );
};

export default RefreshOutcomeRibbon;
